export type SuggestionType = 'address' | 'postcode' | 'area' | 'category' | 'keyword';

export interface SearchSuggestion {
  id: string;
  label: string;
  type: SuggestionType;
  subtitle?: string;
  lat?: number;
  lng?: number;
  score?: number;
  source?: 'dataset' | 'photon' | 'postcode' | 'fallback';
  accuracy?: 'parcel' | 'street' | 'postcode' | 'city';
  streetKey?: string | null;
  houseNumber?: string | null;
}

export interface BuildSuggestionOptions {
  limit?: number;
  popularKeywords?: string[];
}

const SEED_UK_ADDRESSES: SearchSuggestion[] = [
  {
    id: 'sample-1',
    label: '42 Royal Mile, Edinburgh EH1 1SG',
    subtitle: 'Sample address',
    type: 'address',
    accuracy: 'street',
    source: 'dataset',
    score: 0.56
  },
  {
    id: 'sample-2',
    label: '14 George Square, Edinburgh EH8 9LD',
    subtitle: 'Sample address',
    type: 'address',
    accuracy: 'street',
    source: 'dataset',
    score: 0.54
  },
  {
    id: 'partial-1',
    label: 'Northumberland',
    subtitle: 'Area keyword',
    type: 'address',
    accuracy: 'city',
    source: 'dataset',
    score: 0.5
  },
  {
    id: 'partial-2',
    label: 'Gayfield',
    subtitle: 'Area keyword',
    type: 'address',
    accuracy: 'street',
    source: 'dataset',
    score: 0.48
  },
  {
    id: 'sample-3',
    label: '12 Grimmauld Place, London',
    subtitle: 'Sample address',
    type: 'address',
    accuracy: 'street',
    source: 'dataset',
    score: 0.46
  }
];

const PLANNING_TERM_PATTERNS: { label: string; regex: RegExp; variants?: string[] }[] = [
  // --- Residential / Living Types ---
  {
    label: 'student accommodation',
    regex: /\b(pbsa|student(?:\s+accommodation|\s+housing))\b/i,
    variants: [
      'student accommodation',
      'student housing',
      'PBSA',
      'purpose-built student accommodation',
      'purpose built student accommodation'
    ]
  },
  { label: 'build to rent', regex: /\b(build\s*to\s*rent|btr)\b/i },
  { label: 'co-living', regex: /\bco[-\s]?living\b/i },
  { label: 'hmo', regex: /\bhmo\b/i },
  { label: 'care home', regex: /\bcare\s+home\b/i },
  { label: 'short term let / STL', regex: /\b(short\s*term\s*let|stl|holiday\s*let|airbnb|guest\s*house)\b/i },
  { label: 'flat / apartment', regex: /\b(flat|apartment)s?\b/i },
  { label: 'dwellinghouse', regex: /\bdwelling\s*house\b/i },
  { label: 'mews', regex: /\bmews\b/i },
  { label: 'colony house', regex: /\bcolony\s+(house|flat|dwelling)\b/i }, // Edinburgh specific

  // --- Commercial / Business ---
  { label: 'hotel', regex: /\bhotel\b/i },
  { label: 'bar / pub', regex: /\b(bar|public\s+house|pub)\b/i },
  { label: 'restaurant / cafe', regex: /\b(restaurant|cafe|café)\b/i },
  { label: 'hot food takeaway', regex: /\bhot\s+food\s+takeaway\b/i },
  { label: 'retail', regex: /\bretail\b/i },
  { label: 'office', regex: /\boffice\b/i },
  { label: 'co-working', regex: /\bco[-\s]?working\b/i },
  { label: 'mixed use', regex: /\bmixed\s+use\b/i },
  { label: 'nursery / childcare', regex: /\b(nursery|child\s*care|childcare)\b/i },
  { label: 'creche', regex: /\bcreche\b/i },
  { label: 'medical / dental', regex: /\b(clinic|medical|dental|dentist|surgery)\b/i },
  { label: 'veterinary', regex: /\bvets?|veterinar(y|ian)\b/i },
  { label: 'art gallery / studio', regex: /\bart\s+(gallery|studio)\b/i },
  { label: 'gym / fitness', regex: /\b(gym|fitness|leisure)\s*(centre|studio)?\b/i },

  // --- Community / Institutional ---
  { label: 'community facility', regex: /\bcommunity\s+facility\b/i },
  { label: 'community centre', regex: /\bcommunity\s+centre\b/i },
  { label: 'community hall', regex: /\bcommunity\s+hall\b/i },
  { label: 'place of worship', regex: /\b(place\s+of\s+worship|church|mosque|temple)\b/i },
  { label: 'school', regex: /\bschool(s)?\b/i },
  
  // --- Planning / Legal Type ---
  { label: 'change of use', regex: /\bchange\s+of\s+use\b/i },
  { label: 'certificate of lawfulness', regex: /\b(certificate\s+of\s+lawfulness|clued|col)\b/i },
  { label: 'listed building', regex: /\blisted\s+building\b/i },
  { label: 'conservation area', regex: /\bconservation\s+area\b/i },
  { label: 'non-material variation', regex: /\b(non[-\s]?material\s+variation|nma)\b/i },
  { label: 'material variation', regex: /\bmaterial\s+variation\b/i },
  { label: 'subdivision of flats', regex: /\bsub(divid(ed|e|ing)|division)\b/i },
  { label: 'amalgamation of units', regex: /\bamalgamat(e|ion|ing)\b/i },
  { label: 'advertisement consent', regex: /\badvert(isement)?\b/i },
  { label: 'demolition', regex: /\bdemolition\b/i },

  // --- Extensions (General) ---
  { label: 'extension', regex: /\bextension(s)?\b/i },
  { label: 'single-storey extension', regex: /\bsingle[-\s]?storey\s+extension(s)?\b/i },
  { label: 'rear extension', regex: /\brear\s+extension(s)?\b/i },
  { label: 'side extension', regex: /\bside\s+extension(s)?\b/i },
  { label: 'two-storey extension', regex: /\b(two\s*storey|two-storey)\s+extension(s)?\b/i },
  { label: 'part single part two-storey', regex: /\bpart[-\s]?single[-\s]?part[-\s]?two\s+storey\b/i },
  { label: 'wraparound extension', regex: /\bwrap[-\s]?around\s+extension\b/i },
  { label: 'roof extension', regex: /\broof\s+extension(s)?\b/i },
  { label: 'lean-to', regex: /\blean[-\s]?to\b/i },

  // --- Extensions (Basement/Loft) ---
  { label: 'basement extension', regex: /\bbasement\s+extension(s)?\b/i },
  { label: 'basement excavation', regex: /\bbasement\s+(excavat(ed|ion)|dig)\b/i },
  { label: 'basement lightwell', regex: /\b(light\s*well|lightwell)\b/i },
  { label: 'underpinning', regex: /\bunderpin(ning)?\b/i },
  { label: 'loft conversion', regex: /\bloft\s+conversion(s)?\b/i },
  { label: 'attic conversion', regex: /\battic\s+conversion(s)?\b/i },
  { label: 'dormer', regex: /\bdormer(s)?\b/i },
  { label: 'box dormer', regex: /\bbox\s+dormer\b/i },
  { label: 'mansard roof', regex: /\bmansard\b/i },

  // --- External Structures / Garden ---
  { label: 'roof terrace', regex: /\broof\s+terrace\b/i },
  { label: 'balcony', regex: /\bbalcon(y|ies)\b/i },
  { label: 'juliette balcony', regex: /\b(juliet|juliette)\s+balcony\b/i },
  { label: 'decking', regex: /\bdecking\b/i },
  { label: 'patio / hardstanding', regex: /\b(patio|hard\s*standing|hardstanding)\b/i },
  { label: 'conservatory', regex: /\bconservator(y|ies)?\b/i },
  { label: 'sunroom', regex: /\bsun\s*room(s)?\b/i },
  { label: 'orangery', regex: /\boranger(y|ies)\b/i },
  { label: 'porch', regex: /\bporch(es)?\b/i },
  { label: 'canopy', regex: /\bcanop(y|ies)\b/i },
  { label: 'veranda', regex: /\bveranda(h)?\b/i },
  { label: 'pergola', regex: /\bpergola(s)?\b/i },
  { label: 'awnings', regex: /\bawning(s)?\b/i },
  { label: 'garden room', regex: /\bgarden\s+room\b/i },
  { label: 'summerhouse', regex: /\bsummer\s*house(s)?\b/i },
  { label: 'outbuilding', regex: /\boutbuilding(s)?\b/i },
  { label: 'shed', regex: /\b(garden\s+)?shed(s)?\b/i },
  { label: 'studio', regex: /\b(garden\s+|artist\s+)?studio(s)?\b/i },
  { label: 'greenhouse', regex: /\bgreen\s*house\b/i },
  { label: 'polytunnel', regex: /\bpolytunnel(s)?\b/i },
  { label: 'dining shelter', regex: /\bdining\s+shelter\b/i },
  { label: 'bin store', regex: /\bbin\s+store\b/i },
  { label: 'cycle store', regex: /\b(cycle|bike)\s+(store|shelter)\b/i },

  // --- Garages / Parking / Access ---
  { label: 'garage conversion', regex: /\bgarage\s+conversion(s)?\b/i },
  { label: 'garage enlargement', regex: /\bgarage\s+enlargement\b/i },
  { label: 'garage door replacement', regex: /\bgarage\s+door\b/i },
  { label: 'car barn', regex: /\bcar\s+barn\b/i },
  { label: 'car port', regex: /\bcar\s+port(s)?\b/i },
  { label: 'driveway', regex: /\bdriveway(s)?\b/i },
  { label: 'carriage driveway', regex: /\bcarriage\s+driveway\b/i },
  { label: 'dropped kerb', regex: /\b(dropped|lowered)\s+kerb\b/i },
  { label: 'parking', regex: /\bparking\b/i },
  { label: 'access road', regex: /\baccess\s+road\b/i },
  { label: 'highway works', regex: /\b(highway|road)\s+works\b/i },
  { label: 'footpath / pavement', regex: /\b(footpath|pavement)\b/i },

  // --- Windows (Detailed) ---
  { label: 'window replacement', regex: /\bwindow\s+replacement(s)?\b/i },
  { label: 'sash and case', regex: /\bsash\s+(and|&)\s+case\b/i }, // Highly specific to data
  { label: 'timber sash windows', regex: /\btimber\s+sash\s+window(s)?\b/i },
  { label: 'sliding sash', regex: /\bsliding\s+sash\b/i },
  { label: 'casement window', regex: /\bcasement\s+window(s)?\b/i },
  { label: 'upvc windows', regex: /\bupvc\s+window(s)?\b/i },
  { label: 'tilt and turn', regex: /\btilt\s+(and|&)\s+turn\b/i },
  { label: 'replacement windows', regex: /\breplacement\s+window(s)?\b/i },
  { label: 'double glazing', regex: /\b(double\s+glaz(ed|ing)|igu'?s?)\b/i },
  { label: 'triple glazing', regex: /\btriple\s+glaz(ed|ing)\b/i },
  { label: 'secondary glazing', regex: /\bsecondary\s+glaz(ed|ing)\b/i },
  { label: 'slimline glazing', regex: /\bslim(line|lite)\s+glaz(ed|ing)\b/i }, // Heritage specific
  { label: 'heritage glazing', regex: /\bheritage\s+glaz(ed|ing)\b/i },
  { label: 'window shutters', regex: /\b(window\s+)?shutters\b/i },

  // --- Doors ---
  { label: 'door replacement', regex: /\b(replacement\s+door|door\s+replacement)(s)?\b/i },
  { label: 'front door', regex: /\bfront\s+door\b/i },
  { label: 'upvc doors', regex: /\bupvc\s+door(s)?\b/i },
  { label: 'composite door', regex: /\bcomposite\s+door(s)?\b/i },
  { label: 'glazed doors', regex: /\bglazed\s+door(s)?\b/i },
  { label: 'bifold doors', regex: /\bbi[-\s]?fold\s+door(s)?\b/i },
  { label: 'sliding doors', regex: /\bsliding\s+door(s)?\b/i },
  { label: 'patio doors', regex: /\bpatio\s+door(s)?\b/i },
  { label: 'french doors', regex: /\bfrench\s+door(s)?\b/i },
  
  // --- Roofs / Materials / Repair ---
  { label: 're-roofing', regex: /\b(re[-\s]?roof|reroof)(ing)?\b/i },
  { label: 'roof repairs', regex: /\broof\s+repair(s)?\b/i },
  { label: 'flat roof', regex: /\bflat\s+roof\b/i },
  { label: 'pitched roof', regex: /\bpitched\s+roof\b/i },
  { label: 'slate roof', regex: /\bslate(d|s)?\s*(roof)?\b/i },
  { label: 'tiled roof', regex: /\btile(d|s)?\s*(roof)?\b/i },
  { label: 'zinc roof', regex: /\bzinc\s+(roof|clad(ding)?)\b/i },
  { label: 'sedum roof', regex: /\bsedum\s+roof\b/i },
  { label: 'green roof', regex: /\bgreen\s+roof\b/i },
  { label: 'rooflight', regex: /\b(roof\s*light|rooflight|velux|sky\s*light|skylight)s?\b/i },
  { label: 'cupola', regex: /\bcupola\b/i }, // Common in Edinburgh tenements
  { label: 'roof lantern', regex: /\broof\s+lantern\b/i },
  { label: 'sun tunnel', regex: /\bsun\s+tunnel\b/i },
  { label: 'chimney', regex: /\bchimney\b/i },
  { label: 'stone repair', regex: /\bstone\s*(repair|work|cleaning|restore)\b/i },
  { label: 'pointing', regex: /\b(re)?pointing\b/i },
  { label: 'render / roughcast', regex: /\b(render(ing)?|rough\s*cast|pebble\s*dash)\b/i },
  { label: 'cladding', regex: /\bcladding\b/i },
  { label: 'painting', regex: /\bpaint(ing|ed)?\b/i },

  // --- Commercial Signage ---
  { label: 'signage', regex: /\bsignage\b/i },
  { label: 'shopfront', regex: /\bshop\s*front|shopfront\b/i },
  { label: 'fascia sign', regex: /\bfascia\s+sign\b/i },
  { label: 'projecting sign', regex: /\bprojecting\s+sign\b/i },
  { label: 'hanging sign', regex: /\bhanging\s+sign\b/i },
  { label: 'illuminated sign', regex: /\billuminated\s+sign\b/i },
  { label: 'window vinyls', regex: /\b(window\s+)?vinyls\b/i },
  { label: 'menu board', regex: /\bmenu\s+board\b/i },
  { label: 'roller shutters', regex: /\broller\s+shutter(s)?\b/i },
  { label: 'security grille', regex: /\bsecurity\s+grille(s)?\b/i },
  { label: 'atm', regex: /\batm\b/i },
  { label: 'outdoor seating', regex: /\boutdoor\s+seating\b/i },

  // --- Internal / Layout ---
  { label: 'internal alterations', regex: /\binternal\s+alterations\b/i },
  { label: 'open plan', regex: /\bopen\s+plan\b/i },
  { label: 'ensuite', regex: /\ben[-\s]?suite(s)?\b/i },
  { label: 'bathroom', regex: /\b(bathroom|shower\s+room)\b/i },
  { label: 'kitchen', regex: /\bkitchen\b/i },
  { label: 'utility room', regex: /\butility\s+room\b/i },
  { label: 'mezzanine', regex: /\bmezzanine\b/i },
  { label: 'partition removal', regex: /\bpartition\s+remov(al|e)\b/i },
  { label: 'fireplace', regex: /\bfireplace\b/i },
  { label: 'staircase', regex: /\bstair(case|s)?\b/i },
  { label: 'lift', regex: /\blift\s+(shaft|overrun|access)\b/i },

  // --- MEP / Sustainability ---
  { label: 'solar panels', regex: /\bsolar\s+panel(s)?\b/i },
  { label: 'pv panels', regex: /\b(pv|photovoltaic)\s+panel(s)?\b/i },
  { label: 'ev charging', regex: /\b(ev|electric\s+vehicle)\s+(charger|charging|point)\b/i },
  { label: 'heat pump', regex: /\bheat\s+pump(s)?\b/i },
  { label: 'air source heat pump', regex: /\bair\s+source\s+heat\s+pump(s)?|ashp\b/i },
  { label: 'flue / extract', regex: /\b(flue|extract(or)?|extraction)\b/i },
  { label: 'wood burning stove', regex: /\bwood\s*burning\s*stove\b/i },
  { label: 'ventilation', regex: /\b(ventilation|mvhr)\b/i },
  { label: 'air conditioning', regex: /\bair\s+conditioning\b/i },

  // --- Landscaping / Site ---
  { label: 'landscaping', regex: /\blandscap(e|ing)\b/i },
  { label: 'tree works', regex: /\btree\s*(works|removal|felling|pruning|surgery)\b/i },
  { label: 'retaining wall', regex: /\bretaining\s+wall(s)?\b/i },
  { label: 'boundary wall', regex: /\bboundary\s+wall\b/i },
  { label: 'fence', regex: /\bfenc(e|ing)\b/i },
  { label: 'gates', regex: /\bgates?\b/i },
  { label: 'railings', regex: /\brailings\b/i },
  { label: 'steps', regex: /\bsteps\b/i },
  { label: 'paving', regex: /\bpaving\b/i },

  // --- Infrastructure / Telecoms ---
  {
    label: 'telecom mast',
    regex: /\b(telecoms?|telecommunications|phone)\s+(mast|pole|monopole)\b/i,
    variants: [
      'telecom mast',
      'telecom pole',
      'telecom monopole',
      'telecoms mast',
      'telecoms pole',
      'telecommunications mast',
      'phone mast',
      'phone pole',
      'phone monopole',
      'telephone mast',
      'telephone pole',
      'telephone monopole'
    ]
  },
  { label: 'antenna', regex: /\bantenna(e|s)?\b/i },
  { label: 'satellite dish', regex: /\bsatellite\s+dish(es)?\b/i },
  { label: 'broadband cabinet', regex: /\b(telecom|broadband|fibre)\s+cabinet\b/i },
  { label: 'cctv', regex: /\bcctv\b/i },
  { label: 'substation', regex: /\bsubstation\b/i },

  // --- Rural / Other ---
  { label: 'agricultural building', regex: /\bagricultural\s+(building|barn|shed|store)\b/i },
  { label: 'stable', regex: /\b(stable(s)?|equestrian)\b/i },
  { label: 'shipping container', regex: /\bshipping\s+container\b/i },
];
const UK_POSTCODE_REGEX = /([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})/i;

export const DEFAULT_POPULAR_KEYWORDS: string[] = [
  'student housing',
  'solar panels',
  'conservation area',
  'short-term lets',
  'community facility',
  'major development',
  'listed building',
  'change of use',
  'rear extension',
  'loft conversion',
  'parking',
  'access road',
  'heat pump'
];

const STREET_TYPES = [
  'street',
  'road',
  'avenue',
  'lane',
  'drive',
  'place',
  'terrace',
  'crescent',
  'gardens',
  'square',
  'way',
  'close',
  'court',
  'row',
  'wynd',
  'grove',
  'hill',
  'walk',
  'mews',
  'circus',
  'park',
  'green',
  'driveway'
];

const STREET_REGEX = new RegExp(
  `(\\b\\d+[A-Za-z]?\\b)?\\s*([A-Za-z'\\-\\s]+\\b(?:${STREET_TYPES.join('|')})\\b)`,
  'i'
);

const STREET_BOUNDARY_TOKENS = new Set([
  'edinburgh',
  'glasgow',
  'scotland',
  'united',
  'kingdom',
  'uk'
]);

const STREET_STOPWORDS = new Set([
  'flat',
  'flats',
  'apartment',
  'apartments',
  'apt',
  'suite',
  'block',
  'unit',
  'units',
  'gf',
  'ff',
  'sf',
  'tf',
  'basement',
  'rear',
  'front',
  'land',
  'plot'
]);

export interface ParsedAddressMetadata {
  streetKey: string | null;
  houseNumber: string | null;
  streetLabel: string | null;
}

export const parseAddressMetadata = (address: string): ParsedAddressMetadata => {
  if (!address) return { streetKey: null, houseNumber: null, streetLabel: null };

  const trimmed = address.trim();
  if (!trimmed) return { streetKey: null, houseNumber: null, streetLabel: null };

  const regexMatch = trimmed.match(STREET_REGEX);
  if (regexMatch) {
    const houseNumberRaw = regexMatch[1]?.trim() ?? null;
    const streetSegment = regexMatch[2]?.trim() ?? '';
    const streetKey = normalizeText(streetSegment);
    if (streetKey) {
      return {
        streetKey,
        houseNumber: houseNumberRaw ? normalizeText(houseNumberRaw) : null,
        streetLabel: streetSegment || null
      };
    }
  }

  const normalized = normalizeText(trimmed);
  if (!normalized) return { streetKey: null, houseNumber: null, streetLabel: null };

  const tokens = normalized.split(' ');
  let houseNumber: string | null = null;
  const streetTokens: string[] = [];

  for (const token of tokens) {
    if (!houseNumber && /^\d+[a-z]?$/.test(token)) {
      houseNumber = token;
      continue;
    }
    if (STREET_STOPWORDS.has(token)) continue;
    if (STREET_BOUNDARY_TOKENS.has(token)) break;
    streetTokens.push(token);
  }

  if (streetTokens.length === 0) {
    return { streetKey: null, houseNumber, streetLabel: null };
  }

  // Ensure we stop after a recognised street type if present
  let cutoffIndex = streetTokens.length;
  for (let i = 0; i < streetTokens.length; i += 1) {
    if (STREET_TYPES.includes(streetTokens[i])) {
      cutoffIndex = i + 1;
      break;
    }
  }

  const streetKeyTokens = streetTokens.slice(0, cutoffIndex);
  const streetKey = streetKeyTokens.join(' ').trim();

  return {
    streetKey: streetKey ? normalizeText(streetKey) : null,
    houseNumber,
    streetLabel: streetKey || null
  };
};

export const normalizeText = (value: string): string => {
  if (!value) return '';
  return value
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const extractPostcode = (value: string): string | null => {
  if (!value) return null;
  const match = value.toUpperCase().match(UK_POSTCODE_REGEX);
  if (!match) return null;
  const normalized = match[1].toUpperCase().replace(/\s+/g, '');
  if (normalized.length < 5) return null;
  const outcode = normalized.slice(0, normalized.length - 3);
  const incode = normalized.slice(-3);
  return `${outcode} ${incode}`.trim();
};

export const extractPostcodePrefix = (value: string): string | null => {
  const postcode = extractPostcode(value);
  if (!postcode) return null;
  return postcode.split(' ')[0] ?? null;
};

export const computeDistanceKm = (
  latA: number,
  lngA: number,
  latB: number,
  lngB: number
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(latB - latA);
  const dLng = toRad(lngB - lngA);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(latA)) * Math.cos(toRad(latB)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return +(R * c).toFixed(2);
};

const takeTop = <T extends { score?: number }>(items: T[], limit: number): T[] => {
  return items
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, limit);
};

type SearchableApplication = {
  address?: string;
  category?: string;
  ward?: string;
  wards?: string;
  latitude?: number | null;
  lat?: number | null;
  longitude?: number | null;
  lng?: number | null;
};

export const buildTypeaheadSuggestions = (
  apps: SearchableApplication[] = [],
  query: string,
  options: BuildSuggestionOptions = {}
): SearchSuggestion[] => {
  const shuffle = <T,>(arr: T[]): T[] => arr.slice().sort(() => Math.random() - 0.5);
  const toTitleCase = (value: string | null | undefined): string => {
    if (!value) return '';
    return value.split(' ').map(tok => tok ? tok[0].toUpperCase() + tok.slice(1) : '').join(' ').trim();
  };
  const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const limit = options.limit ?? 8;
  const popularKeywords = options.popularKeywords ?? DEFAULT_POPULAR_KEYWORDS;
  const normalizedQuery = normalizeText(query);
  const queryTokens = normalizedQuery ? normalizedQuery.split(' ').filter(Boolean) : [];
  const queryTokenPatterns = queryTokens.map(
    (token) => new RegExp(`(^|[^a-z0-9])${escapeRegex(token)}[a-z0-9]*([^a-z0-9]|$)`)
  );
  const startsWithQuery = (value: string) => value.startsWith(normalizedQuery);
  const matchesAllTokens = (text: string) => {
    if (!queryTokens.length) return true;
    if (!text) return false;
    return queryTokenPatterns.every(pattern => pattern.test(text));
  };
  const looseIncludes = (text: string) => normalizedQuery && text ? text.includes(normalizedQuery) : false;
  if (!normalizedQuery) {
    // Default: blend planning terms + popular keywords + seed UK addresses, interleaved
    const defaultTerms = [...PLANNING_TERM_PATTERNS.map(p => p.label), ...popularKeywords];
    const uniqueTerms = Array.from(new Set(defaultTerms));

    const edinburghAddresses = SEED_UK_ADDRESSES.filter(a => a.label.includes('Edinburgh'));
    const otherAddresses = SEED_UK_ADDRESSES.filter(a => !a.label.includes('Edinburgh'));
    const addressTarget = Math.min(4, limit);
    const addressSuggestions = [
      ...edinburghAddresses,
      ...shuffle(otherAddresses)
    ].slice(0, addressTarget);

    const termSuggestions: SearchSuggestion[] = uniqueTerms.map((term, idx) => ({
      id: `kw-${idx}`,
      label: term,
      type: 'keyword' as const,
      subtitle: 'Popular keyword',
      score: 0.2
    }));

    // Interleave addresses and terms to avoid grouping
    const combined: SearchSuggestion[] = [];
    const maxLen = Math.max(addressSuggestions.length, termSuggestions.length);
    for (let i = 0; i < maxLen && combined.length < limit; i += 1) {
      if (addressSuggestions[i]) combined.push(addressSuggestions[i]);
      if (combined.length >= limit) break;
      if (termSuggestions[i]) combined.push(termSuggestions[i]);
    }

    return combined.slice(0, limit);
  }

  const addressMap = new Map<string, SearchSuggestion>();
  const postcodeMap = new Map<string, SearchSuggestion>();
  const categoryMap = new Map<string, SearchSuggestion>();
  const areaMap = new Map<string, SearchSuggestion>();
  const streetMap = new Map<string, SearchSuggestion>();
  const streetAggregates = new Map<string, { count: number; label: string; sampleLat: number | undefined; sampleLng: number | undefined }>();
  const seededMatches: SearchSuggestion[] = [];

  const maxItemsToScan = Math.min(apps.length, 5000);

  for (let i = 0; i < maxItemsToScan; i += 1) {
    const app = apps[i];
    const addressRaw = app?.address ?? '';
    const categoryRaw = app?.category ?? '';
    const postcode = extractPostcode(addressRaw);
    const outcode = postcode ? postcode.split(' ')[0] : null;

    const normalizedAddress = normalizeText(addressRaw);
    const { streetKey, houseNumber, streetLabel } = parseAddressMetadata(addressRaw);
    if (normalizedAddress && (matchesAllTokens(normalizedAddress) || looseIncludes(normalizedAddress))) {
      if (!addressMap.has(addressRaw)) {
        addressMap.set(addressRaw, {
          id: `addr-${addressMap.size}`,
          label: addressRaw,
          subtitle: 'Address',
          type: 'address',
          lat: app.latitude ?? app.lat ?? undefined,
          lng: app.longitude ?? app.lng ?? undefined,
          score: normalizedQuery.length < 4
            ? 0.65
            : startsWithQuery(normalizedAddress) ? 1.0 : 0.75,
          source: 'dataset',
          accuracy: houseNumber ? 'parcel' : streetKey ? 'street' : 'city',
          streetKey,
          houseNumber
        });
      }
    }

    // Street-level suggestion to avoid over-specific matches
    if (streetKey && (
      (queryTokenPatterns.length > 0 && queryTokenPatterns.every(pattern => pattern.test(streetKey))) ||
      looseIncludes(streetKey)
    )) {
      const displayStreet = streetLabel || toTitleCase(streetKey);
      if (displayStreet && !streetMap.has(streetKey)) {
        streetMap.set(streetKey, {
          id: `street-${streetMap.size}`,
          label: displayStreet,
          subtitle: 'Street',
          type: 'address',
          lat: app.latitude ?? app.lat ?? undefined,
          lng: app.longitude ?? app.lng ?? undefined,
          score: 0.85,
          source: 'dataset',
          accuracy: 'street',
          streetKey,
          houseNumber: null
        });
      }
    }

    if (streetKey) {
      const agg = streetAggregates.get(streetKey);
      if (agg) {
        streetAggregates.set(streetKey, {
          count: agg.count + 1,
          label: agg.label,
          sampleLat: agg.sampleLat ?? app.latitude ?? app.lat ?? undefined,
          sampleLng: agg.sampleLng ?? app.longitude ?? app.lng ?? undefined
        });
      } else {
        streetAggregates.set(streetKey, {
          count: 1,
          label: streetLabel || toTitleCase(streetKey),
          sampleLat: app.latitude ?? app.lat ?? undefined,
          sampleLng: app.longitude ?? app.lng ?? undefined
        });
      }
    }

    if (postcode && (matchesAllTokens(postcode.toLowerCase()) || looseIncludes(postcode.toLowerCase()))) {
      if (!postcodeMap.has(postcode)) {
        postcodeMap.set(postcode, {
          id: `pc-${postcodeMap.size}`,
          label: postcode,
          subtitle: 'Postcode',
          type: 'postcode',
          lat: app.latitude ?? app.lat ?? undefined,
          lng: app.longitude ?? app.lng ?? undefined,
          score: startsWithQuery(postcode.toLowerCase()) ? 0.9 : 0.7,
          source: 'dataset',
          accuracy: 'postcode',
          streetKey: null,
          houseNumber: null
        });
      }
    }

    if (outcode && (matchesAllTokens(outcode.toLowerCase()) || looseIncludes(outcode.toLowerCase()))) {
      if (!areaMap.has(outcode)) {
        areaMap.set(outcode, {
          id: `area-${areaMap.size}`,
          label: outcode,
          subtitle: 'Area',
          type: 'area',
          score: startsWithQuery(outcode.toLowerCase()) ? 0.6 : 0.5,
          source: 'dataset',
          accuracy: 'postcode',
          streetKey: null,
          houseNumber: null
        });
      }
    }

    const normalizedCategory = normalizeText(categoryRaw);
    if (normalizedCategory && (matchesAllTokens(normalizedCategory) || looseIncludes(normalizedCategory))) {
      if (!categoryMap.has(categoryRaw)) {
        categoryMap.set(categoryRaw, {
          id: `cat-${categoryMap.size}`,
          label: categoryRaw,
          subtitle: 'Category',
          type: 'category',
          score: startsWithQuery(normalizedCategory) ? 0.8 : 0.6
        });
      }
    }
  }

  const keywordMatches = popularKeywords
    .filter((term) => matchesAllTokens(normalizeText(term)))
    .map((term, idx) => ({
      id: `kw-${idx}`,
      label: term,
      type: 'keyword' as const,
      subtitle: 'Popular keyword',
      score: startsWithQuery(normalizeText(term)) ? 0.5 : 0.4
    }));

  const derivePlanningVariants = (pattern: { label: string; regex: RegExp; variants?: string[] }): string[] => {
    if (pattern.variants && pattern.variants.length > 0) {
      return pattern.variants;
    }

    const src = pattern.regex.source;
    if (!src.includes('|')) {
      return [pattern.label];
    }

    type Part = { type: 'text'; value: string } | { type: 'group'; alts: string[] };
    const parts: Part[] = [];
    let lastIndex = 0;
    const groupRegex = /\(([^()]+)\)/g;
    let m: RegExpExecArray | null;

    while ((m = groupRegex.exec(src)) !== null) {
      const before = src.slice(lastIndex, m.index);
      if (before) {
        parts.push({ type: 'text', value: before });
      }
      const body = m[1];
      if (body.includes('|')) {
        parts.push({ type: 'group', alts: body.split('|') });
      } else {
        parts.push({ type: 'text', value: body });
      }
      lastIndex = m.index + m[0].length;
    }
    const after = src.slice(lastIndex);
    if (after) {
      parts.push({ type: 'text', value: after });
    }

    if (!parts.some(p => p.type === 'group')) {
      return [pattern.label];
    }

    const rawPhrases: string[] = [];
    const build = (index: number, current: string) => {
      if (index >= parts.length) {
        rawPhrases.push(current);
        return;
      }
      const part = parts[index];
      if (part.type === 'text') {
        build(index + 1, current + part.value);
      } else {
        part.alts.forEach(alt => {
          build(index + 1, current + alt);
        });
      }
    };
    build(0, '');

    const cleanAndTitleCase = (raw: string): string | null => {
      const cleaned = normalizeText(
        raw
          .replace(/\\b/g, ' ')
          .replace(/\\s\+/g, ' ')
          .replace(/\\s\*/g, ' ')
          .replace(/\\s\?/g, ' ')
          .replace(/\\s/g, ' ')
          .replace(/\?/g, '')
          .replace(/\+/g, '')
          .replace(/\*/g, '')
          .replace(/\\./g, '')
      );
      if (!cleaned) return null;
      return cleaned.split(' ').map(tok => (tok ? tok[0].toUpperCase() + tok.slice(1) : '')).join(' ').trim();
    };

    const variants: string[] = [];
    rawPhrases.forEach(phrase => {
      const v = cleanAndTitleCase(phrase);
      if (!v) return;
      if (!variants.includes(v)) variants.push(v);
    });

    if (!variants.length) {
      return [pattern.label];
    }

    if (!variants.includes(pattern.label)) {
      variants.unshift(pattern.label);
    }

    return variants;
  };

  // Planning term suggestions: include label and any configured variants so typing a related word
  // (e.g. "telecoms") can surface other useful phrases like "phone mast" or "telecom pole".
  const planningTermMatches: SearchSuggestion[] = [];
  PLANNING_TERM_PATTERNS.forEach((pattern, idx) => {
    const baseLabel = pattern.label;
    const normalizedLabel = normalizeText(baseLabel);

    const variants = derivePlanningVariants(pattern);

    const planningQuery = normalizedQuery.replace(/s\b/, '');
    const planningTokens = planningQuery
      ? planningQuery.split(' ').filter(Boolean)
      : [];

    const variantHits = variants.filter(variant => {
      const norm = normalizeText(variant);
      if (!planningTokens.length) return false;
      return planningTokens.every(token => norm.includes(token));
    });

    if (variantHits.length === 0) return;

    const added = new Set<string>();

    variantHits.forEach((variant, vIndex) => {
      const norm = normalizeText(variant);
      const key = norm;
      if (added.has(key)) return;
      added.add(key);
      planningTermMatches.push({
        id: `planterm-${idx}-${vIndex}`,
        label: variant,
        type: 'keyword',
        subtitle: 'Planning term',
        score: startsWithQuery(norm) ? 0.95 : 0.8
      });
    });

    const baseKey = normalizedLabel;
    if (!added.has(baseKey)) {
      planningTermMatches.push({
        id: `planterm-${idx}-base`,
        label: baseLabel,
        type: 'keyword',
        subtitle: 'Planning term',
        score: startsWithQuery(normalizedLabel) ? 0.9 : 0.75
      });
    }
  });

  const streetAggregateSuggestions: SearchSuggestion[] = Array.from(streetAggregates.entries())
    .filter(([key]) => queryTokenPatterns.length === 0 || queryTokenPatterns.every(pattern => pattern.test(key)))
    .map(([key, agg], idx) => ({
      id: `street-agg-${idx}`,
      label: agg.count > 1 ? `${agg.label} (${agg.count} matches)` : agg.label,
      subtitle: 'Street',
      type: 'address',
      lat: agg.sampleLat,
      lng: agg.sampleLng,
      score: 0.8,
      source: 'dataset',
      accuracy: 'street',
      streetKey: key,
      houseNumber: null
    }));

  // Seeded suggestions (non-empty query): include helpful seed addresses/areas that loosely match the input
  if (normalizedQuery) {
    SEED_UK_ADDRESSES.forEach(seed => {
      const normSeed = normalizeText(seed.label);
      if (matchesAllTokens(normSeed) || looseIncludes(normSeed)) {
        seededMatches.push({
          ...seed,
          score: Math.max(seed.score ?? 0.5, 0.52) // small boost so they appear but below strong dataset hits
        });
      }
    });
  }

  let suggestions: SearchSuggestion[] = [
    ...seededMatches,
    // Prefer street-level suggestions ahead of generic addresses
    ...streetMap.values(),
    ...streetAggregateSuggestions,
    ...addressMap.values(),
    ...postcodeMap.values(),
    ...areaMap.values(),
    ...categoryMap.values(),
    ...keywordMatches,
    ...planningTermMatches
  ];

  // If strict word-start matching yields nothing, fall back to a looser contains match
  // so partial inputs like "northum" still show something while keeping primary matching precise.
  if (suggestions.length === 0 && normalizedQuery) {
    const loose: SearchSuggestion[] = [];
    const maxLooseScan = Math.min(apps.length, 5000);
    for (let i = 0; i < maxLooseScan && loose.length < limit; i += 1) {
      const app = apps[i];
      const rawAddress = app?.address ?? '';
      const normAddr = normalizeText(rawAddress);
      if (normAddr.includes(normalizedQuery)) {
        const { streetKey, houseNumber } = parseAddressMetadata(rawAddress);
        loose.push({
          id: `addr-loose-${loose.length}`,
          label: rawAddress,
          subtitle: 'Address',
          type: 'address',
          lat: app.latitude ?? app.lat ?? undefined,
          lng: app.longitude ?? app.lng ?? undefined,
          score: 0.55,
          source: 'dataset',
          accuracy: houseNumber ? 'parcel' : streetKey ? 'street' : 'city',
          streetKey,
          houseNumber
        });
      }
    }
    suggestions = loose;
  }

  if (suggestions.length === 0) {
    return popularKeywords.slice(0, limit).map((term, idx) => ({
      id: `kw-${idx}`,
      label: term,
      type: 'keyword' as const,
      subtitle: 'Popular keyword',
      score: 0.3
    }));
  }

  return takeTop(suggestions, limit);
};

export const clampRadius = (value: number, min = 0, max = 25): number => {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return Math.round(value * 100) / 100;
};

export const formatRadiusLabel = (radiusKm: number): string => {
  if (radiusKm === 0) {
    return 'Exact address only';
  }
  if (radiusKm < 1) {
    return `${Math.round(radiusKm * 1000)} m`;
  }
  return `${radiusKm.toFixed(radiusKm % 1 === 0 ? 0 : 1)} km`;
};

export const isValidLatLng = (lat?: number | null, lng?: number | null): boolean => {
  if (lat == null || lng == null) return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};
